import isEqual from 'lodash/isEqual';
import { useEffect, useMemo, useRef, useState } from 'react';

import { ContentPlacement } from '@candu-ai/placement/types';
import { editorViewportNodeCss } from 'craft/containers/Viewport/modes/components';
import { useCurrentSnapshot, usePlacementMode, useViewportDom } from 'craft/state';

import { createTargetElementForSelector } from './createTargetElementForSelector';
import { syncPlaceholderStyles } from './placeholderStyleOverrides';

export const CANDU_INLINE_PLACEHOLDER = 'CANDU_INLINE_PLACEHOLDER';

interface UseSyncInlineDocumentProps {
  iframeRef?: HTMLIFrameElement;
  syncIterator?: number;
}

const resetSyncedDimensions = (viewportElement: HTMLElement) => {
  /* eslint-disable no-param-reassign */
  viewportElement.style.top = '';
  viewportElement.style.left = '';
  viewportElement.style.width = '';
};

const toggleDocumentVisibility = (viewportElement: HTMLElement, isVisible: boolean) => {
  /* eslint-disable no-param-reassign */
  viewportElement.style.display = isVisible ? '' : 'none';
};

const getSelector = (
  iframeDocument: HTMLIFrameElement,
  selector: string,
): HTMLElement | null | undefined => {
  try {
    return iframeDocument?.contentDocument?.querySelector(selector);
  } catch (e) {
    return undefined;
  }
};

const syncDimensions = (
  documentElement: HTMLElement,
  snapshotPlaceholder: HTMLElement,
  viewportElement: HTMLElement,
) => {
  const placeholderInner = snapshotPlaceholder.children[1];

  /* eslint-disable no-param-reassign */
  // sync size document element to placeholder, dimensions are an option, but it's likely more reliable to dump the HTML
  if (!placeholderInner?.shadowRoot) {
    return;
  }

  const shadowStyles = placeholderInner.shadowRoot.getElementById('shadow-styled-styles');

  if (shadowStyles) {
    const styledComponentCssRules: CSSRuleList = (document.querySelector(
      'style[data-styled="active"]',
    ) as any)?.sheet?.cssRules;
    let styledComponentsParsedStyles = '';
    Object.keys(styledComponentCssRules).forEach((key) => {
      styledComponentsParsedStyles += `${styledComponentCssRules[key as any].cssText} \n`;
    });

    shadowStyles.innerHTML = `<style>${styledComponentsParsedStyles}</style>`;
  }

  const elementRoot = placeholderInner.shadowRoot.getElementById('element-root') as HTMLElement;

  elementRoot.innerHTML = documentElement.outerHTML || '';

  // sync snapshot dimensions back to the editor node
  const placeholderPosition = snapshotPlaceholder.getBoundingClientRect();

  if (
    placeholderPosition.width === 0 &&
    placeholderPosition.top === 0 &&
    placeholderPosition.left === 0
  ) {
    // in this case the placeholder is not visible, so we shouldn't apply these values
    return;
  }

  viewportElement.style.top = `${placeholderPosition.top}px`;
  viewportElement.style.left = `${placeholderPosition.left}px`;
  viewportElement.style.width = `${placeholderPosition.width}px`;
};

export const useSyncInlineDocument = ({ iframeRef, syncIterator }: UseSyncInlineDocumentProps) => {
  const { placement } = useCurrentSnapshot();
  const { documentEl, viewportContentEl, viewportWrapperEl } = useViewportDom();
  const { previewSelector, previewSelectorLocation, placementMode } = usePlacementMode();

  const [activePlacement, setActivePlacement] = useState<ContentPlacement | undefined>(placement);
  const cleanupRef = useRef<VoidFunction | undefined>(undefined);

  useEffect(() => {
    // when we are previewing a placement, or the preview is canceled
    // update state's active placement in real time
    const placementWithChanges = placement
      ? {
          ...placement,
          selector: previewSelector || placement?.selector || '',
          location: previewSelectorLocation || placement?.location,
        }
      : placement;

    const isPlacementCanceled = !previewSelector && !previewSelectorLocation;

    setActivePlacement({
      ...placementWithChanges,
      // a little hacky but needed to detect a change in syncInlineDocument to update the placeholder,
      // because when we canceled editing the placement is the same as the original
      ...(isPlacementCanceled || previewSelectorLocation === placement?.location
        ? { reset: true }
        : {}),
    } as ContentPlacement);
  }, [previewSelector, previewSelectorLocation]);

  useEffect(() => {
    if (viewportContentEl) {
      toggleDocumentVisibility(viewportContentEl, !placementMode);
    }
  }, [placementMode]);

  useEffect(() => {
    // when the saved placement changes, we want to make sure to update the active placement
    if (!isEqual(activePlacement, placement)) {
      // a little hacky but needed to detect a change in syncInlineDocument to update the placeholder,
      // because when synced the placement is the same as the original
      setActivePlacement({ ...placement, reset: true } as ContentPlacement);
    }
  }, [placement]);

  useEffect(() => {
    if (!viewportWrapperEl || !documentEl || !viewportContentEl) {
      return () => null;
    }

    // make sure to recalculate beacon position when the viewportContainer is resized
    const observer = new ResizeObserver(() => {
      if (iframeRef?.contentDocument) {
        const placeholder = iframeRef.contentDocument.getElementById(CANDU_INLINE_PLACEHOLDER);
        if (placeholder) {
          syncDimensions(documentEl, placeholder, viewportContentEl);
        }
      }
    });
    observer.observe(viewportWrapperEl);

    return () => observer.disconnect();
  }, [viewportWrapperEl, documentEl, viewportContentEl, iframeRef]);

  const isTargetVisible = useMemo(() => {
    if (!syncIterator || !iframeRef || !iframeRef.contentDocument) {
      return false;
    }

    const root = getSelector(iframeRef, activePlacement?.selector || '');

    if (!documentEl || !viewportContentEl) {
      return false;
    }
    let placeholder = iframeRef.contentDocument.getElementById(CANDU_INLINE_PLACEHOLDER);

    if ((!root || !isEqual(placement, activePlacement) || placementMode) && !!placeholder) {
      cleanupRef.current?.();

      resetSyncedDimensions(viewportContentEl);

      // if this was a "forced" reset, we don't need that key anymore so remove it
      if (Object.keys(activePlacement || {}).includes('reset')) {
        const activePlacementWithoutReset = { ...activePlacement } as ContentPlacement & {
          reset?: boolean;
        };
        delete activePlacementWithoutReset.reset;
        setActivePlacement(activePlacementWithoutReset);
      }

      placeholder = iframeRef.contentDocument.getElementById(CANDU_INLINE_PLACEHOLDER);
    }

    if (!root || !activePlacement || placementMode) {
      cleanupRef.current = undefined;
      return false;
    }

    if (!placeholder) {
      // using a custom element prevents nth-of-type selectors from having weirdness
      placeholder = iframeRef.contentDocument.createElement('candu-placeholder');
      placeholder.id = CANDU_INLINE_PLACEHOLDER;
      placeholder.style.all = 'initial';
      placeholder.style.display = 'block';
      placeholder.style.opacity = '0';
      placeholder.innerHTML = document.getElementById('application-fonts')?.innerHTML || '';

      const placeholderInner = iframeRef.contentDocument.createElement('div');
      placeholderInner.id = `${CANDU_INLINE_PLACEHOLDER}_INNER`;

      const shadowRoot = placeholderInner.attachShadow({ mode: 'open' });

      const stylesRoot = iframeRef.contentDocument.createElement('div');
      stylesRoot.id = 'shadow-styles';
      stylesRoot.innerHTML = document.getElementById('application-stylesheet')?.innerHTML || '';
      stylesRoot.innerHTML += `<style>${editorViewportNodeCss}</style>`;
      stylesRoot.innerHTML += `<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/bootstrap-reboot@4.5.6/reboot.min.css" crossorigin="anonymous" referrerpolicy="no-referrer" />`;
      stylesRoot.innerHTML += `<link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/flexboxgrid/6.3.1/flexboxgrid.min.css" integrity="sha512-YHuwZabI2zi0k7c9vtg8dK/63QB0hLvD4thw44dFo/TfBFVVQOqEG9WpviaEpbyvgOIYLXF1n7xDUfU3GDs0sw==" crossorigin="anonymous" referrerpolicy="no-referrer" />`;
      stylesRoot.innerHTML += `<link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/sanitize.css/2.0.0/sanitize.min.css" integrity="sha512-uZEpdg8O5w6a2kJ9VKX6MJPr5MIj+R85f7706fR5D+dlr91P0sWQq3mBL/O/cQ8MfAOavO7VjUoJCH55BNsLDw==" crossorigin="anonymous" referrerpolicy="no-referrer" />`;
      stylesRoot.innerHTML += syncPlaceholderStyles;

      const styledStylesRoot = iframeRef.contentDocument.createElement('div');
      styledStylesRoot.id = 'shadow-styled-styles';

      const elementRoot = iframeRef.contentDocument.createElement('div');
      elementRoot.id = 'element-root';

      shadowRoot.appendChild(stylesRoot);
      shadowRoot.appendChild(styledStylesRoot);
      shadowRoot.appendChild(elementRoot);

      placeholder.appendChild(placeholderInner);

      const { cleanup } = createTargetElementForSelector(placeholder, root, activePlacement);
      cleanupRef.current = () => {
        cleanup();
      };
    }

    syncDimensions(documentEl, placeholder, viewportContentEl);

    return root.style.display !== 'none';
  }, [
    syncIterator,
    iframeRef,
    activePlacement,
    cleanupRef.current,
    documentEl,
    viewportContentEl,
    placementMode,
  ]);

  return { isTargetVisible };
};
